import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, AlertTriangle, User, Briefcase, Phone, Target, AlertCircle, Download, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';

interface CallAnalysisReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  report: any;
  candidateId: string;
  requirementId: string;
  candidateName?: string;
  requirementTitle?: string;
}

export const CallAnalysisReportModal: React.FC<CallAnalysisReportModalProps> = ({
  isOpen,
  onClose,
  report,
  candidateId: _candidateId,
  requirementId: _requirementId,
  candidateName,
  requirementTitle
}) => {
  // Note: candidateId and requirementId are kept for potential future use
  void _candidateId;
  void _requirementId;
  const [isDownloading, setIsDownloading] = useState(false);
  const { toast } = useToast();

  // Extract names from report if not provided as props
  const displayCandidateName = candidateName || 
    report?.report_header?.candidate_name ||
    report?.candidate_info?.name || 
    report?.call_metadata?.candidate_name ||
    'Candidate';
  
  // Prioritize report data over props (report has accurate position from AI analysis)
  const displayRequirementTitle = 
    report?.report_header?.position ||
    report?.requirement_info?.title || 
    report?.call_metadata?.requirement_title ||
    (requirementTitle && requirementTitle !== 'Unknown Requirement' ? requirementTitle : null) ||
    'Position';
  
  // New: Extract recruiter name and other header info
  const displayRecruiterName = report?.report_header?.recruiter_name || 'Recruiter';
  const displayInterviewDate = report?.report_header?.interview_date || new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const displayDuration = report?.report_header?.duration_minutes || report?.call_metadata?.total_duration_formatted || '0';
  const displayCallsAnalyzed = report?.report_header?.calls_analyzed || report?.call_metadata?.calls_analyzed || 1;

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      // Create a printable version of the report
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast({
          title: 'Error',
          description: 'Please allow popups to download PDF',
          variant: 'error'
        });
        return;
      }

      const exec = report.executive_summary || {};
      const candidate = report.candidate_assessment || {};
      const recruiter = report.recruiter_performance || {};
      const logistics = report.candidate_logistics || report.job_fitment_hygiene || {};
      const risk = report.risk_assessment || {};
      const nextSteps = report.next_steps || {};
      const overallSkills = report.overall_skill_analysis || {};
      const commAspects = report.communication_aspects || {};
      const behavioralTraits = report.behavioral_cognitive_traits || {};
      const conversationAnalysis = report.conversation_analysis || {};

      // Helper function to get recommendation label for PDF
      const getPdfRecommendationLabel = (rec: string) => {
        switch (rec) {
          case 'HIGHLY_RECOMMENDED': case 'STRONG_YES': return '✨ Highly Recommended';
          case 'RECOMMENDED': case 'YES': return '✓ Recommended';
          case 'WORTH_CONSIDERING': case 'MAYBE': return '◐ Worth Considering';
          case 'NOT_RECOMMENDED': case 'NO': return '⚠ Not Recommended for This Role';
          case 'NEEDS_REASSESSMENT': case 'STRONG_NO': return '↺ Needs Reassessment';
          default: return rec || 'N/A';
        }
      };

      const getPdfActionLabel = (action: string) => {
        switch (action) {
          case 'PROCEED_TO_TECHNICAL': case 'SCHEDULE_TECHNICAL': return '✓ Proceed to Technical Interview';
          case 'PROCEED_TO_CLIENT': case 'SCHEDULE_CLIENT': return '✓ Proceed to Client Interview';
          case 'ADDITIONAL_SCREENING': case 'NEED_MORE_INFO': return '↺ Additional Screening Needed';
          case 'KEEP_IN_PIPELINE': case 'HOLD': return '📋 Keep in Pipeline';
          case 'REASSESS_FOR_OTHER_ROLES': return '🔄 Consider for Other Roles';
          case 'NOT_SUITABLE_FOR_ROLE': case 'REJECT': return '⚠ Not Suitable for This Role';
          default: return action || 'N/A';
        }
      };

      // Generate report ID
      const reportId = `CSR-${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}-${displayCandidateName.split(' ').map((n: string) => n[0]).join('').toUpperCase()}-001`;
      
      // Helper for score colors
      const getScoreClass = (score: number) => score >= 70 ? 'good' : score >= 45 ? 'average' : 'poor';
      const getRiskColor = (level: string) => level === 'LOW' ? '#22c55e' : level === 'MODERATE' ? '#f59e0b' : '#ef4444';

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Candidate Screening Report - ${displayCandidateName}</title>
          <style>
            :root {
              --primary: #4f46e5;
              --primary-light: #818cf8;
              --primary-dark: #3730a3;
              --success: #22c55e;
              --success-light: #dcfce7;
              --warning: #f59e0b;
              --warning-light: #fef3c7;
              --danger: #ef4444;
              --danger-light: #fee2e2;
              --gray-50: #f9fafb;
              --gray-100: #f3f4f6;
              --gray-200: #e5e7eb;
              --gray-500: #6b7280;
              --gray-700: #374151;
              --gray-900: #111827;
            }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif; 
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              min-height: 100vh;
              padding: 20px;
              color: var(--gray-900);
              line-height: 1.6;
            }
            .report-container {
              max-width: 1000px;
              margin: 0 auto;
              background: white;
              border-radius: 16px;
              box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
              overflow: hidden;
            }
            .report-header {
              background: linear-gradient(135deg, #1e1b4b 0%, #312e81 100%);
              color: white;
              padding: 30px 40px;
            }
            .header-top {
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 24px;
              padding-bottom: 20px;
              border-bottom: 1px solid rgba(255,255,255,0.2);
            }
            .company-logo {
              display: flex;
              align-items: center;
              gap: 12px;
            }
            .logo-icon {
              width: 48px;
              height: 48px;
              background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
              border-radius: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 20px;
              font-weight: 700;
            }
            .company-name { font-size: 20px; font-weight: 700; }
            .company-tagline { font-size: 12px; opacity: 0.7; }
            .report-id-box { text-align: right; }
            .report-id-label { font-size: 11px; opacity: 0.7; text-transform: uppercase; letter-spacing: 1px; }
            .report-id-value { font-size: 14px; font-weight: 500; }
            .report-title {
              display: flex;
              align-items: center;
              gap: 12px;
              font-size: 24px;
              font-weight: 700;
              margin-bottom: 20px;
            }
            .header-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 20px;
            }
            .header-item .label { font-size: 11px; opacity: 0.7; text-transform: uppercase; letter-spacing: 0.5px; }
            .header-item .value { font-size: 15px; font-weight: 600; }
            .report-content { padding: 30px 40px; }
            .scores-section {
              background: var(--gray-50);
              border-radius: 12px;
              padding: 24px;
              margin-bottom: 24px;
            }
            .scores-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 16px;
              margin-bottom: 20px;
            }
            .score-card {
              background: white;
              border-radius: 12px;
              padding: 20px;
              text-align: center;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              border: 1px solid var(--gray-200);
            }
            .score-value { font-size: 36px; font-weight: 700; }
            .score-value.good { color: var(--success); }
            .score-value.average { color: var(--warning); }
            .score-value.poor { color: var(--danger); }
            .score-label { font-size: 12px; color: var(--gray-500); text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px; }
            .score-level { font-size: 13px; font-weight: 600; margin-top: 8px; padding: 4px 12px; border-radius: 20px; display: inline-block; }
            .score-level.good { background: var(--success-light); color: #166534; }
            .score-level.average { background: var(--warning-light); color: #92400e; }
            .score-level.poor { background: var(--danger-light); color: #dc2626; }
            .recommendation-box {
              border-radius: 12px;
              padding: 16px 24px;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }
            .recommendation-box.positive { background: linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%); border: 2px solid #22c55e; }
            .recommendation-box.neutral { background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border: 2px solid #f59e0b; }
            .recommendation-box.negative { background: linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%); border: 2px solid #f97316; }
            .recommendation-text { font-size: 18px; font-weight: 600; display: flex; align-items: center; gap: 8px; }
            .recommendation-box.positive .recommendation-text { color: #166534; }
            .recommendation-box.neutral .recommendation-text { color: #92400e; }
            .recommendation-box.negative .recommendation-text { color: #c2410c; }
            .confidence-badge { background: white; padding: 6px 16px; border-radius: 20px; font-size: 14px; font-weight: 600; }
            .summary-quote {
              background: #eff6ff;
              border-left: 4px solid var(--primary);
              padding: 16px 20px;
              margin-top: 20px;
              border-radius: 0 8px 8px 0;
              font-style: italic;
              color: var(--gray-700);
            }
            .section { margin-bottom: 24px; }
            .section-header {
              display: flex;
              align-items: center;
              gap: 10px;
              font-size: 18px;
              font-weight: 600;
              color: var(--gray-900);
              margin-bottom: 16px;
              padding-bottom: 10px;
              border-bottom: 2px solid var(--gray-200);
            }
            .fitment-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
            .fitment-card { background: var(--gray-50); border-radius: 8px; padding: 16px; border: 1px solid var(--gray-200); }
            .fitment-label { font-size: 11px; color: var(--gray-500); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
            .fitment-value { font-size: 15px; font-weight: 600; color: var(--gray-900); }
            .rating-table { width: 100%; border-collapse: collapse; border: 1px solid var(--gray-200); border-radius: 8px; overflow: hidden; }
            .rating-table th, .rating-table td { padding: 12px 16px; text-align: left; border-bottom: 1px solid var(--gray-200); }
            .rating-table th { background: var(--gray-100); font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); }
            .rating-table tr:last-child td { border-bottom: none; }
            .stars { color: var(--warning); font-size: 14px; letter-spacing: 2px; }
            .rating-badge { display: inline-block; padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; margin-left: 8px; }
            .rating-badge.good { background: var(--success-light); color: #166534; }
            .rating-badge.average { background: var(--warning-light); color: #92400e; }
            .rating-badge.poor { background: var(--danger-light); color: #dc2626; }
            .section-score { background: var(--gray-100); padding: 12px 20px; border-radius: 8px; margin-top: 16px; display: flex; align-items: center; justify-content: space-between; border: 1px solid var(--gray-200); }
            .conversation-card { border: 1px solid var(--gray-200); border-radius: 12px; margin-bottom: 16px; overflow: hidden; }
            .conversation-header { background: var(--gray-50); padding: 12px 16px; border-bottom: 1px solid var(--gray-200); display: flex; align-items: center; justify-content: space-between; }
            .skill-name { font-weight: 600; color: var(--primary); font-size: 15px; }
            .response-badge { font-size: 12px; font-weight: 600; padding: 4px 12px; border-radius: 12px; }
            .response-badge.good { background: var(--success-light); color: #166534; }
            .response-badge.partial { background: var(--warning-light); color: #92400e; }
            .response-badge.poor { background: var(--danger-light); color: #dc2626; }
            .conversation-body { padding: 16px; }
            .qa-block { margin-bottom: 16px; }
            .qa-label { font-size: 12px; font-weight: 600; color: var(--gray-500); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
            .qa-text { background: var(--gray-50); padding: 12px; border-radius: 8px; font-size: 14px; border: 1px solid var(--gray-200); }
            .eval-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin-top: 16px; }
            .eval-item { text-align: center; padding: 10px 8px; background: var(--gray-50); border-radius: 8px; border: 1px solid var(--gray-200); }
            .eval-item-label { font-size: 10px; color: var(--gray-500); text-transform: uppercase; margin-bottom: 4px; }
            .eval-item-stars { font-size: 12px; color: var(--warning); }
            .assessment-note { background: #eff6ff; border-left: 3px solid var(--primary); padding: 12px; margin-top: 12px; border-radius: 0 8px 8px 0; font-size: 13px; }
            .critical-gaps { background: var(--danger-light); border: 1px solid #fecaca; border-radius: 8px; padding: 16px; }
            .gap-tag { display: inline-block; background: white; border: 1px solid #fecaca; color: var(--danger); padding: 4px 12px; border-radius: 16px; font-size: 13px; font-weight: 500; margin-right: 8px; }
            .score-formula { background: var(--gray-900); color: white; padding: 20px; border-radius: 8px; text-align: center; font-family: monospace; font-size: 18px; margin-bottom: 20px; }
            .breakdown-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
            .breakdown-box { padding: 16px; border-radius: 8px; }
            .breakdown-box.deductions { background: var(--danger-light); border: 1px solid #fecaca; }
            .breakdown-box.additions { background: var(--success-light); border: 1px solid #bbf7d0; }
            .breakdown-item { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid rgba(0,0,0,0.1); font-size: 14px; }
            .progress-bar-container { margin-bottom: 12px; }
            .progress-label { display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 13px; }
            .progress-bar { height: 10px; background: var(--gray-200); border-radius: 5px; overflow: hidden; }
            .progress-fill { height: 100%; border-radius: 5px; }
            .recruiter-scores { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 20px; }
            .recruiter-score-card { background: var(--gray-50); padding: 16px; border-radius: 8px; text-align: center; border: 1px solid var(--gray-200); }
            .recruiter-score-value { font-size: 24px; font-weight: 700; color: var(--primary); }
            .recruiter-score-label { font-size: 10px; color: var(--gray-500); text-transform: uppercase; margin-top: 4px; }
            .sw-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
            .sw-box { padding: 16px; border-radius: 8px; }
            .sw-box.strengths { background: var(--success-light); border: 1px solid #bbf7d0; }
            .sw-box.weaknesses { background: var(--warning-light); border: 1px solid #fde68a; }
            .risk-badge { font-size: 16px; font-weight: 600; padding: 8px 20px; border-radius: 20px; display: inline-block; }
            .risk-badge.low { background: var(--success-light); color: #166534; }
            .risk-badge.moderate { background: var(--warning-light); color: #92400e; }
            .risk-badge.high { background: var(--danger-light); color: #dc2626; }
            .next-action-box { border-radius: 12px; padding: 20px; margin-bottom: 20px; display: flex; align-items: center; justify-content: space-between; }
            .next-action-box.positive { background: linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%); border: 2px solid #22c55e; }
            .next-action-box.negative { background: linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%); border: 2px solid #f97316; }
            .focus-tag { display: inline-block; background: #dbeafe; color: #1e40af; padding: 8px 16px; border-radius: 8px; font-weight: 500; border: 1px solid #bfdbfe; margin: 4px; }
            .candidate-feedback { background: var(--success-light); border-radius: 8px; padding: 16px; margin-top: 20px; border: 1px solid #bbf7d0; }
            .report-footer { background: var(--gray-50); padding: 20px 40px; border-top: 1px solid var(--gray-200); display: flex; justify-content: space-between; align-items: center; }
            .footer-info { font-size: 12px; color: var(--gray-500); }
            @media print { 
              body { background: white; padding: 0; } 
              .report-container { box-shadow: none; border-radius: 0; }
              .section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="report-container">
            <!-- Header -->
            <div class="report-header">
              <div class="header-top">
                <div class="company-logo">
                  <div class="logo-icon">RE</div>
                  <div>
                    <div class="company-name">RecruitEdge</div>
                    <div class="company-tagline">AI-Powered Recruitment Platform</div>
          </div>
              </div>
                <div class="report-id-box">
                  <div class="report-id-label">Report ID</div>
                  <div class="report-id-value">${reportId}</div>
              </div>
              </div>
              <div class="report-title">📋 Candidate Screening Report</div>
              <div class="header-grid">
                <div class="header-item">
                  <div class="label">CANDIDATE</div>
                  <div class="value">${displayCandidateName}</div>
            </div>
                <div class="header-item">
                  <div class="label">POSITION</div>
                  <div class="value">${displayRequirementTitle}</div>
            </div>
                <div class="header-item">
                  <div class="label">RECRUITER</div>
                  <div class="value">${displayRecruiterName}</div>
                </div>
                <div class="header-item">
                  <div class="label">INTERVIEW DATE</div>
                  <div class="value">${displayInterviewDate}</div>
                </div>
                <div class="header-item">
                  <div class="label">DURATION</div>
                  <div class="value">${displayDuration} minutes</div>
                </div>
                <div class="header-item">
                  <div class="label">CALLS ANALYZED</div>
                  <div class="value">${displayCallsAnalyzed} call${displayCallsAnalyzed > 1 ? 's' : ''}</div>
                </div>
              </div>
          </div>

            <div class="report-content">
              <!-- Score Cards Section -->
              <div class="scores-section">
                <div class="scores-grid">
                  <div class="score-card">
                    <div class="score-value ${getScoreClass(overallSkills.technical?.score || exec.overall_score || 0)}">${overallSkills.technical?.score || exec.overall_score || 0}</div>
                    <div class="score-label">Technical</div>
                    <span class="score-level ${getScoreClass(overallSkills.technical?.score || exec.overall_score || 0)}">${overallSkills.technical?.level || (exec.overall_score >= 70 ? 'Good' : exec.overall_score >= 45 ? 'Average' : 'Poor')}</span>
              </div>
                  <div class="score-card">
                    <div class="score-value ${getScoreClass(overallSkills.communication?.score || 50)}">${overallSkills.communication?.score || 50}</div>
                    <div class="score-label">Communication</div>
                    <span class="score-level ${getScoreClass(overallSkills.communication?.score || 50)}">${overallSkills.communication?.level || 'Average'}</span>
              </div>
                  <div class="score-card">
                    <div class="score-value ${getScoreClass(overallSkills.behavioral?.score || 50)}">${overallSkills.behavioral?.score || 50}</div>
                    <div class="score-label">Behavioral</div>
                    <span class="score-level ${getScoreClass(overallSkills.behavioral?.score || 50)}">${overallSkills.behavioral?.level || 'Average'}</span>
              </div>
                  <div class="score-card">
                    <div class="score-value" style="color: ${getRiskColor(risk.overall_risk_level || risk.risk_level || 'MODERATE')}">${risk.overall_risk_level || risk.risk_level || 'MODERATE'}</div>
                    <div class="score-label">Risk Level</div>
                    <span class="score-level ${(risk.overall_risk_level || risk.risk_level) === 'LOW' ? 'good' : (risk.overall_risk_level || risk.risk_level) === 'MODERATE' ? 'average' : 'poor'}">${(risk.overall_risk_level || risk.risk_level) === 'LOW' ? 'Good' : (risk.overall_risk_level || risk.risk_level) === 'HIGH' ? 'Concern' : 'Moderate'}</span>
            </div>
                </div>

                <div class="recommendation-box ${exec.hire_recommendation?.includes('RECOMMENDED') || exec.hire_recommendation?.includes('YES') ? 'positive' : exec.hire_recommendation?.includes('CONSIDERING') || exec.hire_recommendation?.includes('MAYBE') ? 'neutral' : 'negative'}">
                  <div class="recommendation-text">${getPdfRecommendationLabel(exec.hire_recommendation)}</div>
                  <span class="confidence-badge" style="color: ${exec.confidence_level === 'HIGH' ? '#166534' : exec.confidence_level === 'MEDIUM' ? '#92400e' : '#dc2626'}; border: 1px solid ${exec.confidence_level === 'HIGH' ? '#86efac' : exec.confidence_level === 'MEDIUM' ? '#fde68a' : '#fecaca'};">${exec.confidence_level || 'MEDIUM'} Confidence</span>
                </div>

                ${exec.one_line_summary ? `<div class="summary-quote">"${exec.one_line_summary}"</div>` : ''}
              </div>

              ${exec.recommendation_reasoning ? `
              <div style="background: #f8fafc; border-left: 4px solid var(--primary); padding: 15px 20px; margin-bottom: 24px; border-radius: 0 8px 8px 0;">
                <strong style="color: var(--primary);">Why This Recommendation:</strong><br/>
                ${exec.recommendation_reasoning}
              </div>` : ''}

              ${exec.detailed_summary ? `<p style="margin-bottom: 24px; color: var(--gray-700);">${exec.detailed_summary}</p>` : ''}

              ${exec.key_highlights?.length ? `
              <div class="section">
                <div class="section-header">✓ Strengths & Highlights</div>
                <ul style="list-style: none; padding: 0;">
                  ${exec.key_highlights.map((h: string) => `<li style="padding: 8px 12px; background: var(--success-light); margin-bottom: 8px; border-radius: 6px; color: #166534;">✓ ${h}</li>`).join('')}
                </ul>
              </div>` : ''}

              ${exec.key_concerns?.length ? `
              <div class="section">
                <div class="section-header">◐ Areas to Explore</div>
                <ul style="list-style: none; padding: 0;">
                  ${exec.key_concerns.map((c: string) => `<li style="padding: 8px 12px; background: var(--warning-light); margin-bottom: 8px; border-radius: 6px; color: #92400e;">◐ ${c}</li>`).join('')}
                </ul>
              </div>` : ''}

              ${exec.improvement_suggestions?.length ? `
              <div class="section">
                <div class="section-header">💡 Suggestions for Improvement</div>
                <ul style="list-style: none; padding: 0;">
                  ${exec.improvement_suggestions.map((s: string) => `<li style="padding: 8px 12px; background: #e0e7ff; margin-bottom: 8px; border-radius: 6px; color: #3730a3;">→ ${s}</li>`).join('')}
                </ul>
          </div>` : ''}

          ${Object.keys(logistics).length > 0 ? `
          <div class="section">
            <div class="section-header">💼 Job Fitment & Hygiene</div>
            <div class="fitment-grid">
              ${logistics.current_ctc ? `<div class="fitment-card"><div class="fitment-label">Current CTC</div><div class="fitment-value">${logistics.current_ctc}</div></div>` : ''}
              ${logistics.expected_ctc ? `<div class="fitment-card"><div class="fitment-label">Expected CTC</div><div class="fitment-value">${logistics.expected_ctc}</div></div>` : ''}
              ${logistics.notice_period ? `<div class="fitment-card"><div class="fitment-label">Notice Period</div><div class="fitment-value">${logistics.notice_period}</div></div>` : ''}
              ${logistics.preferred_location || logistics.location_preference ? `<div class="fitment-card"><div class="fitment-label">Preferred Location</div><div class="fitment-value">${logistics.preferred_location || logistics.location_preference}</div></div>` : ''}
              ${logistics.relocation_willingness ? `<div class="fitment-card"><div class="fitment-label">Relocation</div><div class="fitment-value">${logistics.relocation_willingness === 'WILLING' ? '✓ Willing' : logistics.relocation_willingness}</div></div>` : ''}
              ${logistics.availability ? `<div class="fitment-card"><div class="fitment-label">Availability</div><div class="fitment-value">${logistics.availability}</div></div>` : ''}
              ${logistics.current_company ? `<div class="fitment-card"><div class="fitment-label">Current Company</div><div class="fitment-value">${logistics.current_company}</div></div>` : ''}
              ${logistics.total_experience ? `<div class="fitment-card"><div class="fitment-label">Total Experience</div><div class="fitment-value">${logistics.total_experience}</div></div>` : ''}
              ${logistics.reason_for_change ? `<div class="fitment-card"><div class="fitment-label">Reason for Change</div><div class="fitment-value">${logistics.reason_for_change}</div></div>` : ''}
            </div>
          </div>` : ''}

          ${commAspects.overall_score ? `
          <div class="section">
            <div class="section-header">🗣️ Communication Aspects</div>
            <table class="rating-table">
              <tr><th style="width: 20%;">Parameter</th><th style="width: 25%;">Rating</th><th style="width: 55%;">Observation</th></tr>
              ${['clarity', 'fluency', 'confidence', 'listening', 'articulation', 'professional_tone'].map(param => {
                const data = commAspects[param];
                if (!data) return '';
                const label = param.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                const rating = (data.rating || 'N/A').toLowerCase();
                const stars = rating.includes('excellent') ? '★★★★★' : rating.includes('good') ? '★★★★☆' : rating.includes('average') ? '★★★☆☆' : rating.includes('below') || rating.includes('poor') ? '★★☆☆☆' : '★★★☆☆';
                const badgeColor = rating.includes('excellent') ? '#059669' : rating.includes('good') ? '#22c55e' : rating.includes('average') ? '#f59e0b' : '#ef4444';
                const badgeBg = rating.includes('excellent') ? '#d1fae5' : rating.includes('good') ? '#dcfce7' : rating.includes('average') ? '#fef3c7' : '#fee2e2';
                return `<tr><td><strong>${label}</strong></td><td><span style="color: #f59e0b; letter-spacing: 2px;">${stars}</span> <span style="background: ${badgeBg}; color: ${badgeColor}; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: 600; margin-left: 5px;">${data.rating || 'N/A'}</span></td><td style="font-size: 13px; color: #6b7280;">${data.observation || '-'}</td></tr>`;
              }).join('')}
              <tr style="background: #f8fafc;"><td><strong>Overall Score</strong></td><td colspan="2"><strong style="color: ${commAspects.overall_score >= 70 ? '#22c55e' : commAspects.overall_score >= 45 ? '#f59e0b' : '#ef4444'};">${commAspects.overall_score}/100 (${commAspects.overall_level || 'N/A'})</strong></td></tr>
            </table>
            ${commAspects.summary ? `<p style="font-style: italic; color: #6b7280; margin-top: 10px;">${commAspects.summary}</p>` : ''}
          </div>` : ''}

          ${behavioralTraits.overall_score ? `
          <div class="section">
            <div class="section-header">🧠 Behavioral & Cognitive Traits</div>
            <table class="rating-table">
              <tr><th style="width: 20%;">Trait</th><th style="width: 25%;">Rating</th><th style="width: 55%;">Evidence</th></tr>
              ${['proactiveness', 'critical_thinking', 'adaptability', 'team_orientation', 'problem_solving', 'learning_agility', 'ownership'].map(trait => {
                const data = behavioralTraits[trait];
                if (!data) return '';
                const label = trait.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                const rating = (data.rating || 'N/A').toLowerCase();
                const stars = rating.includes('excellent') ? '★★★★★' : rating.includes('good') ? '★★★★☆' : rating.includes('average') ? '★★★☆☆' : rating.includes('below') || rating.includes('poor') ? '★★☆☆☆' : '★★★☆☆';
                const badgeColor = rating.includes('excellent') ? '#059669' : rating.includes('good') ? '#22c55e' : rating.includes('average') ? '#f59e0b' : '#ef4444';
                const badgeBg = rating.includes('excellent') ? '#d1fae5' : rating.includes('good') ? '#dcfce7' : rating.includes('average') ? '#fef3c7' : '#fee2e2';
                return `<tr><td><strong>${label}</strong></td><td><span style="color: #f59e0b; letter-spacing: 2px;">${stars}</span> <span style="background: ${badgeBg}; color: ${badgeColor}; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: 600; margin-left: 5px;">${data.rating || 'N/A'}</span></td><td style="font-size: 13px; color: #6b7280;">${data.evidence || '-'}</td></tr>`;
              }).join('')}
              <tr style="background: #f8fafc;"><td><strong>Overall Score</strong></td><td colspan="2"><strong style="color: ${behavioralTraits.overall_score >= 70 ? '#22c55e' : behavioralTraits.overall_score >= 45 ? '#f59e0b' : '#ef4444'};">${behavioralTraits.overall_score}/100 (${behavioralTraits.overall_level || 'N/A'})</strong></td></tr>
            </table>
            ${behavioralTraits.summary ? `<p style="font-style: italic; color: #6b7280; margin-top: 10px;">${behavioralTraits.summary}</p>` : ''}
          </div>` : ''}

          ${conversationAnalysis.question_responses?.length ? `
          <div class="section">
            <div class="section-header">💬 Conversation Analysis <span style="font-weight: normal; font-size: 14px; color: var(--gray-500); margin-left: auto;">${conversationAnalysis.total_questions_analyzed || conversationAnalysis.question_responses?.length || 0} Questions Analyzed</span></div>
            ${conversationAnalysis.question_responses.map((qa: any) => {
              const getEvalStars = (rating: string) => {
                const r = (rating || '').toLowerCase();
                if (r.includes('excellent')) return { stars: '★★★★★', color: '#059669' };
                if (r.includes('good')) return { stars: '★★★★☆', color: '#22c55e' };
                if (r.includes('average')) return { stars: '★★★☆☆', color: '#f59e0b' };
                return { stars: '★★☆☆☆', color: '#ef4444' };
              };
              return `
              <div style="border: 1px solid #e5e7eb; border-radius: 8px; margin-bottom: 15px; overflow: hidden; ${qa.response_quality === 'NOT_ANSWERED' || qa.response_quality === 'POOR' ? 'border-color: #fca5a5; background: #fef2f2;' : ''}">
                <div style="background: ${qa.response_quality === 'NOT_ANSWERED' || qa.response_quality === 'POOR' ? '#fee2e2' : '#f3f4f6'}; padding: 10px 14px; display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #4f46e5; font-size: 14px;">📌 SKILL: ${qa.skill}</strong>
                  <span style="padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: bold; background: ${qa.response_quality === 'EXCELLENT' || qa.response_quality === 'GOOD' ? '#dcfce7; color: #166534' : qa.response_quality === 'PARTIAL' ? '#fef3c7; color: #92400e' : '#fee2e2; color: #dc2626'};">${qa.response_quality === 'GOOD' ? '✓ GOOD' : qa.response_quality === 'EXCELLENT' ? '✨ EXCELLENT' : qa.response_quality === 'PARTIAL' ? '⚠️ PARTIAL' : '❌ ' + qa.response_quality}</span>
                </div>
                <div style="padding: 14px;">
                  <div style="margin-bottom: 10px;">
                    <div style="font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; margin-bottom: 4px;">❓ Question Asked</div>
                    <div style="background: #f9fafb; padding: 10px; border-radius: 6px; font-size: 13px; border: 1px solid #e5e7eb;">"${qa.question_asked}"</div>
                </div>
                  <div style="margin-bottom: 10px;">
                    <div style="font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; margin-bottom: 4px;">💬 Candidate Response</div>
                    <div style="background: #f9fafb; padding: 10px; border-radius: 6px; font-size: 13px; border: 1px solid #e5e7eb;">"${qa.candidate_response}"</div>
              </div>
                  ${qa.evaluation_scores ? `
                  <div style="display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin: 14px 0;">
                    ${['technical_depth', 'conceptual_clarity', 'problem_solving', 'real_world_application', 'use_of_examples'].map(metric => {
                      const score = qa.evaluation_scores?.[metric];
                      if (!score) return '';
                      const label = metric.replace(/_/g, ' ').substring(0, 12);
                      const evalData = getEvalStars(score.rating || score);
                      return `
                      <div style="text-align: center; padding: 8px; background: #f9fafb; border-radius: 6px; border: 1px solid #e5e7eb;">
                        <div style="font-size: 9px; color: #6b7280; text-transform: uppercase; margin-bottom: 4px;">${label}</div>
                        <div style="color: #f59e0b; font-size: 11px;">${evalData.stars}</div>
                        <div style="font-size: 10px; font-weight: 600; color: ${evalData.color}; margin-top: 2px;">${score.rating || score}</div>
                      </div>`;
                    }).join('')}
                  </div>` : ''}
                  ${qa.overall_assessment ? `
                  <div style="background: #eff6ff; border-left: 3px solid #4f46e5; padding: 10px; border-radius: 0 6px 6px 0; font-size: 12px;">
                    <strong style="color: #4f46e5;">💡 Assessment:</strong> ${qa.overall_assessment}
                  </div>` : ''}
                </div>
              </div>`;
            }).join('')}
            ${conversationAnalysis.summary ? `
            <div style="background: #f3f4f6; padding: 12px; border-radius: 8px; display: flex; justify-content: space-around; text-align: center;">
              <div><strong style="font-size: 20px;">${conversationAnalysis.summary.total_questions || 0}</strong><br/><small>Total</small></div>
              <div><strong style="font-size: 20px; color: #16a34a;">${conversationAnalysis.summary.answered_well || 0}</strong><br/><small>Good</small></div>
              <div><strong style="font-size: 20px; color: #ca8a04;">${conversationAnalysis.summary.answered_partially || 0}</strong><br/><small>Partial</small></div>
              <div><strong style="font-size: 20px; color: #dc2626;">${conversationAnalysis.summary.not_answered || 0}</strong><br/><small>Gap</small></div>
            </div>` : ''}
          </div>` : ''}

          ${report.skills_scorecard ? `
          <div class="section">
            <h2>📊 Skills Scorecard <span style="font-weight: normal; font-size: 13px; color: #6b7280; margin-left: 10px;">Based on JD Requirements</span></h2>
            
            ${report.skills_scorecard.primary_skills?.length ? `
            <h4 style="margin: 16px 0 10px 0; color: #374151;">Primary Skills (${report.skills_scorecard.primary_skills_weight || 80}% weight) — Score: <span style="color: ${(report.skills_scorecard.primary_skills_score || 0) >= 60 ? '#22c55e' : (report.skills_scorecard.primary_skills_score || 0) >= 30 ? '#f59e0b' : '#ef4444'};">${report.skills_scorecard.primary_skills_score || 0}/100</span></h4>
            <table style="width: 100%; border-collapse: collapse; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; margin-bottom: 16px;">
              <tr style="background: #f3f4f6;">
                <th style="padding: 10px; text-align: left; font-size: 11px; text-transform: uppercase; color: #6b7280;">Skill</th>
                <th style="padding: 10px; text-align: left; font-size: 11px; text-transform: uppercase; color: #6b7280;">Importance</th>
                <th style="padding: 10px; text-align: left; font-size: 11px; text-transform: uppercase; color: #6b7280;">Status</th>
                <th style="padding: 10px; text-align: left; font-size: 11px; text-transform: uppercase; color: #6b7280;">Score</th>
                <th style="padding: 10px; text-align: left; font-size: 11px; text-transform: uppercase; color: #6b7280;">Impact</th>
              </tr>
              ${report.skills_scorecard.primary_skills.map((skill: any) => {
                const impColor = skill.importance === 'CRITICAL' ? '#dc2626' : skill.importance === 'HIGH' ? '#c2410c' : '#92400e';
                const impBg = skill.importance === 'CRITICAL' ? '#fee2e2' : skill.importance === 'HIGH' ? '#ffedd5' : '#fef3c7';
                const demonstrated = skill.demonstrated;
                const rowBg = !demonstrated && skill.importance === 'CRITICAL' ? 'background: #fef2f2;' : '';
                const maxPts = skill.weight_percent || (skill.importance === 'CRITICAL' ? 40 : skill.importance === 'HIGH' ? 25 : 15);
                const impactMatch = (skill.score_impact || '0').match(/([+-]?\d+)/);
                const impactPts = impactMatch ? parseInt(impactMatch[1]) : 0;
                const impactDisplay = impactPts >= 0 ? '+' + Math.abs(impactPts) + '/' + maxPts : impactPts + '/' + maxPts;
                const statusHtml = demonstrated ? '<span style="color: #22c55e;">✓</span> Demonstrated' : '<span style="color: #dc2626;">✗</span> <span style="color: #dc2626; font-weight: 600;">Not Demonstrated</span>';
                const scoreColor = skill.score >= 60 ? '#22c55e' : skill.score >= 30 ? '#f59e0b' : '#dc2626';
                const impactColor = impactPts >= 0 ? '#22c55e' : '#dc2626';
                return '<tr style="' + rowBg + ' border-bottom: 1px solid #e5e7eb;">' +
                  '<td style="padding: 10px;"><strong>' + skill.skill_name + '</strong></td>' +
                  '<td style="padding: 10px;"><span style="background: ' + impBg + '; color: ' + impColor + '; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 600; text-transform: uppercase;">' + skill.importance + '</span></td>' +
                  '<td style="padding: 10px;">' + statusHtml + '</td>' +
                  '<td style="padding: 10px;"><span style="font-weight: 600; color: ' + scoreColor + ';">' + (skill.score || 0) + '%</span></td>' +
                  '<td style="padding: 10px;"><span style="color: ' + impactColor + '; font-weight: 600;">' + impactDisplay + ' pts</span></td>' +
                '</tr>';
              }).join('')}
            </table>` : ''}
            
            ${report.skills_scorecard.secondary_skills?.length ? `
            <h4 style="margin: 20px 0 10px 0; color: #374151;">Secondary Skills (${report.skills_scorecard.secondary_skills_weight || 20}% weight) — Score: <span style="color: ${(report.skills_scorecard.secondary_skills_score || 0) >= 60 ? '#22c55e' : (report.skills_scorecard.secondary_skills_score || 0) >= 30 ? '#f59e0b' : '#ef4444'};">${report.skills_scorecard.secondary_skills_score || 0}/100</span></h4>
            <table style="width: 100%; border-collapse: collapse; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; margin-bottom: 16px;">
              <tr style="background: #f3f4f6;">
                <th style="padding: 10px; text-align: left; font-size: 11px; text-transform: uppercase; color: #6b7280;">Skill</th>
                <th style="padding: 10px; text-align: left; font-size: 11px; text-transform: uppercase; color: #6b7280;">Status</th>
                <th style="padding: 10px; text-align: left; font-size: 11px; text-transform: uppercase; color: #6b7280;">Score</th>
                <th style="padding: 10px; text-align: left; font-size: 11px; text-transform: uppercase; color: #6b7280;">Impact</th>
              </tr>
              ${report.skills_scorecard.secondary_skills.map((skill: any) => {
                const maxPts = skill.weight_percent || 15;
                const impactMatch = (skill.score_impact || '0').match(/([+-]?\d+)/);
                const impactPts = impactMatch ? parseInt(impactMatch[1]) : 0;
                const impactDisplay = impactPts >= 0 ? '+' + Math.abs(impactPts) + '/' + maxPts : impactPts + '/' + maxPts;
                return '<tr style="border-bottom: 1px solid #e5e7eb;">' +
                  '<td style="padding: 10px;"><strong>' + skill.skill_name + '</strong></td>' +
                  '<td style="padding: 10px;">' + (skill.demonstrated ? '<span style="color: #22c55e;">✓</span> Demonstrated' : '<span style="color: #f59e0b;">○</span> Not Shown') + '</td>' +
                  '<td style="padding: 10px;"><span style="font-weight: 600; color: ' + (skill.score >= 60 ? '#22c55e' : skill.score >= 30 ? '#f59e0b' : '#dc2626') + ';">' + (skill.score || 0) + '%</span></td>' +
                  '<td style="padding: 10px;"><span style="color: ' + (impactPts >= 0 ? '#22c55e' : '#dc2626') + '; font-weight: 600;">' + impactDisplay + ' pts</span></td>' +
                '</tr>';
              }).join('')}
            </table>` : ''}
            
            ${report.skills_scorecard.critical_gaps?.length ? `
            <div style="background: #fee2e2; border: 1px solid #fecaca; border-radius: 8px; padding: 16px; margin-top: 16px;">
              <div style="color: #dc2626; font-weight: 600; margin-bottom: 8px;">⚠️ Critical Skill Gaps Identified</div>
              <div>${report.skills_scorecard.critical_gaps.map((g: string) => `<span style="display: inline-block; background: white; border: 1px solid #fecaca; color: #dc2626; padding: 4px 12px; border-radius: 16px; font-size: 13px; margin: 3px;">${g}</span>`).join('')}</div>
              <div style="margin-top: 8px; font-size: 12px; color: #dc2626;">— Must be verified in next round</div>
            </div>` : ''}
          </div>` : ''}

          ${report.score_calculation ? `
          <div class="section">
            <h2>📐 Score Calculation Breakdown</h2>
            <div style="background: #1f2937; color: white; padding: 20px; border-radius: 8px; text-align: center; font-family: monospace; font-size: 18px; margin-bottom: 20px;">
              ${report.score_calculation.calculation_formula || `Base: 100 → Final: ${report.score_calculation.final_score || 0}`}
              <div style="font-size: 36px; font-weight: bold; margin-top: 10px;">${report.score_calculation.final_score || 0}%</div>
            </div>
            
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px;">
              <div style="background: #fee2e2; border: 1px solid #fecaca; border-radius: 8px; padding: 16px;">
                <h4 style="color: #dc2626; margin: 0 0 12px 0;">📉 Deductions</h4>
                ${report.score_calculation.deductions?.length ? report.score_calculation.deductions.map((d: any) => `
                  <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid rgba(0,0,0,0.1); font-size: 13px;">
                    <span>${d.reason}</span>
                    <span style="color: #dc2626; font-weight: bold;">${d.points} pts</span>
                  </div>
                `).join('') : '<p style="color: #666; font-style: italic;">No deductions</p>'}
              </div>
              <div style="background: #dcfce7; border: 1px solid #bbf7d0; border-radius: 8px; padding: 16px;">
                <h4 style="color: #166534; margin: 0 0 12px 0;">📈 Bonuses</h4>
                ${report.score_calculation.additions?.length ? report.score_calculation.additions.map((a: any) => `
                  <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid rgba(0,0,0,0.1); font-size: 13px;">
                    <span>${a.reason}</span>
                    <span style="color: #166534; font-weight: bold;">+${a.points} pts</span>
                  </div>
                `).join('') : '<p style="color: #666; font-style: italic;">No bonuses earned</p>'}
              </div>
            </div>
            
            ${report.score_calculation.score_breakdown ? `
            <h4 style="margin: 20px 0 12px 0;">Score Contribution Breakdown</h4>
            ${Object.entries(report.score_calculation.score_breakdown).map(([key, value]: [string, any]) => {
              const label = key.replace(/_/g, ' ').replace(/contribution$/i, '').trim().replace(/\b\w/g, (l: string) => l.toUpperCase());
              const numValue = Number(value) || 0;
              const barWidth = Math.abs(numValue) * 2;
              const barColor = numValue >= 0 ? '#22c55e' : '#ef4444';
              return `
              <div style="margin-bottom: 12px;">
                <div style="display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 13px;">
                  <span>${label}</span>
                  <span style="font-weight: bold; color: ${numValue >= 0 ? '#166534' : '#dc2626'};">${numValue >= 0 ? '+' : ''}${numValue} pts</span>
                </div>
                <div style="height: 10px; background: #e5e7eb; border-radius: 5px; overflow: hidden;">
                  <div style="height: 100%; width: ${Math.min(barWidth, 100)}%; background: ${barColor}; border-radius: 5px;"></div>
                </div>
              </div>`;
            }).join('')}` : ''}
          </div>` : ''}

          <div class="section">
            <div class="section-header">📊 Candidate Assessment</div>
            <table class="rating-table">
              <tr><th>Metric</th><th>Score</th><th>Assessment</th></tr>
              <tr><td>Overall Fit</td><td><strong>${candidate.overall_fit_score || candidate.skills_match_score || 0}%</strong></td><td>${candidate.fit_explanation || '-'}</td></tr>
              <tr><td>Skills Match</td><td>${candidate.skills_match_score || 0}%</td><td>${candidate.skills_analysis || '-'}</td></tr>
              <tr><td>Experience Match</td><td>${candidate.experience_match_score || 0}%</td><td>${candidate.experience_analysis || '-'}</td></tr>
              <tr><td>Technical Depth</td><td>${candidate.technical_depth_score || 0}%</td><td>${candidate.technical_assessment || '-'}</td></tr>
              <tr><td>Communication</td><td>${candidate.communication_score || 0}%</td><td>${candidate.communication_assessment || '-'}</td></tr>
              <tr><td>Experience Level</td><td colspan="2">${candidate.experience_level || 'N/A'} ${candidate.growth_potential ? `| Growth Potential: ${candidate.growth_potential}` : ''}</td></tr>
            </table>
            ${candidate.mandatory_skills_covered?.length ? `<h3>✓ Skills Confirmed</h3><div>${candidate.mandatory_skills_covered.map((s: string) => `<span class="skills-tag">${s}</span>`).join('')}</div>` : ''}
            ${candidate.skill_gaps?.length ? `<h3>◐ Skills to Verify</h3><div>${candidate.skill_gaps.map((s: string) => `<span class="gaps-tag">${s}</span>`).join('')}</div>` : ''}
            ${candidate.strengths_summary ? `<h3>Core Strengths</h3><p>${candidate.strengths_summary}</p>` : ''}
            ${candidate.development_areas ? `<h3>Development Areas</h3><p>${candidate.development_areas}</p>` : ''}
          </div>

          <div class="section">
            <div class="section-header">👤 Recruiter Performance</div>
            <div class="recruiter-scores">
              <div class="recruiter-score-card">
                <div class="recruiter-score-value">${recruiter.overall_call_quality || recruiter.call_quality_score || 0}%</div>
                <div class="recruiter-score-label">Call Quality</div>
              </div>
              <div class="recruiter-score-card">
                <div class="recruiter-score-value">${recruiter.questioning_technique_score || 0}%</div>
                <div class="recruiter-score-label">Questions</div>
              </div>
              <div class="recruiter-score-card">
                <div class="recruiter-score-value">${recruiter.active_listening_score || 0}%</div>
                <div class="recruiter-score-label">Listening</div>
              </div>
              <div class="recruiter-score-card">
                <div class="recruiter-score-value">${recruiter.professionalism_score || 0}%</div>
                <div class="recruiter-score-label">Professional</div>
              </div>
              <div class="recruiter-score-card">
                <div class="recruiter-score-value">${recruiter.information_extraction_score || recruiter.script_adherence_score || 0}%</div>
                <div class="recruiter-score-label">Extraction</div>
              </div>
            </div>
            <div class="sw-grid">
              ${recruiter.strengths?.length ? `
              <div class="sw-box strengths">
                <div style="font-weight: 600; color: #166534; margin-bottom: 12px;">✓ Strengths</div>
                ${recruiter.strengths.map((s: string) => `<div style="font-size: 14px; padding: 6px 0;">• ${s}</div>`).join('')}
              </div>` : '<div></div>'}
              ${recruiter.areas_for_improvement?.length ? `
              <div class="sw-box weaknesses">
                <div style="font-weight: 600; color: #92400e; margin-bottom: 12px;">⚠️ Areas for Improvement</div>
                ${recruiter.areas_for_improvement.map((a: string) => `<div style="font-size: 14px; padding: 6px 0;">• ${a}</div>`).join('')}
              </div>` : '<div></div>'}
            </div>
          </div>

          <div class="section">
            <div class="section-header">📋 Candidate Logistics</div>
            <table class="rating-table">
              <tr>
                <td><strong>Interest Level</strong></td>
                <td><span style="padding: 4px 12px; border-radius: 12px; background: ${logistics.interest_level === 'HIGH' || logistics.interest_level === 'INTERESTED' ? 'var(--success-light); color: #166534' : 'var(--warning-light); color: #92400e'}; font-weight: 600;">${logistics.interest_level || 'OPEN'}</span></td>
                <td style="font-size: 13px; color: var(--gray-500);">${logistics.interest_indicators || ''}</td>
              </tr>
              <tr>
                <td><strong>Notice Period</strong></td>
                <td>${logistics.notice_period || 'N/A'}</td>
                <td style="font-size: 13px; color: var(--gray-500);">${logistics.notice_period_flexibility || ''}</td>
              </tr>
              <tr>
                <td><strong>Current CTC</strong></td>
                <td>${logistics.current_ctc || 'Not discussed'}</td>
                <td rowspan="2" style="font-size: 13px; color: var(--gray-500);">${logistics.ctc_negotiability || ''}</td>
              </tr>
              <tr>
                <td><strong>Expected CTC</strong></td>
                <td>${logistics.expected_ctc || 'Not discussed'}</td>
              </tr>
              <tr>
                <td><strong>Location</strong></td>
                <td>${logistics.location_preference || 'N/A'}</td>
                <td>Relocation: <span style="font-weight: 600; color: ${logistics.relocation_willingness === 'WILLING' ? '#166534' : '#92400e'};">${logistics.relocation_willingness || 'N/A'}</span></td>
              </tr>
              <tr>
                <td><strong>Availability</strong></td>
                <td colspan="2">${logistics.availability || 'N/A'}</td>
              </tr>
            </table>
          </div>

          <div class="section">
            <div class="section-header">⚠️ Risk Assessment</div>
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 20px;">
              <span>Overall Risk Level:</span>
              <span class="risk-badge ${(risk.overall_risk_level || risk.risk_level || '').toLowerCase() === 'low' ? 'low' : (risk.overall_risk_level || risk.risk_level || '').toLowerCase() === 'high' || (risk.overall_risk_level || risk.risk_level || '').toLowerCase() === 'elevated' ? 'high' : 'moderate'}">${(risk.overall_risk_level || risk.risk_level) === 'LOW' ? '🟢' : (risk.overall_risk_level || risk.risk_level) === 'HIGH' ? '🔴' : '🟡'} ${risk.overall_risk_level || risk.risk_level || 'MODERATE'}</span>
              ${risk.risk_summary ? `<span style="font-size: 14px; color: var(--gray-500);">— ${risk.risk_summary}</span>` : ''}
            </div>

            ${risk.concerns?.length ? `
            <div style="background: var(--warning-light); border-radius: 8px; padding: 16px; margin-bottom: 16px; border: 1px solid #fde68a;">
              <strong style="color: #92400e; display: block; margin-bottom: 12px;">⚠️ Concerns to Address</strong>
              ${risk.concerns.map((c: any) => `
                <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid rgba(0,0,0,0.1);">
                  <span><strong>${typeof c === 'string' ? c : c.concern}</strong></span>
                  ${typeof c === 'object' && c.mitigation_suggestion ? `<span style="font-size: 12px; color: var(--gray-500);">${c.mitigation_suggestion}</span>` : ''}
                </div>
              `).join('')}
            </div>` : ''}

            ${risk.positive_indicators?.length ? `
            <div style="margin-top: 16px;">
              <h4 style="margin-bottom: 12px; color: var(--gray-700);">✓ Positive Indicators</h4>
              <div style="display: flex; flex-wrap: wrap; gap: 8px;">
                ${risk.positive_indicators.map((p: string) => `<span style="background: var(--success-light); color: #166534; padding: 6px 12px; border-radius: 16px; font-size: 13px; border: 1px solid #bbf7d0;">✓ ${p}</span>`).join('')}
              </div>
            </div>` : ''}
          </div>

          <div class="section">
            <div class="section-header">📋 Next Steps & Recommendations</div>
            
            <div class="next-action-box ${exec.hire_recommendation?.includes('RECOMMENDED') || exec.hire_recommendation?.includes('YES') ? 'positive' : 'negative'}">
              <div style="font-size: 20px; font-weight: 600; color: ${exec.hire_recommendation?.includes('RECOMMENDED') || exec.hire_recommendation?.includes('YES') ? '#166534' : '#c2410c'};">${getPdfActionLabel(nextSteps.primary_recommendation || nextSteps.recommended_action)}</div>
              <span style="background: ${nextSteps.urgency === 'HIGH' ? 'var(--danger)' : nextSteps.urgency === 'MEDIUM' ? 'var(--warning)' : 'var(--success)'}; color: white; padding: 6px 16px; border-radius: 16px; font-size: 12px; font-weight: 600; text-transform: uppercase;">${nextSteps.urgency === 'HIGH' ? '🔴' : nextSteps.urgency === 'MEDIUM' ? '🟡' : '🟢'} ${nextSteps.urgency || 'MEDIUM'} Priority</span>
          </div>

            ${nextSteps.recommendation_details ? `<p style="margin-bottom: 20px; color: var(--gray-600);">${nextSteps.recommendation_details}</p>` : ''}
            ${nextSteps.alternative_recommendation ? `<p style="margin-bottom: 20px; font-style: italic; color: var(--gray-600);"><strong>Alternative:</strong> ${nextSteps.alternative_recommendation}</p>` : ''}

            ${nextSteps.interview_focus_areas?.length ? `
            <h4 style="margin: 20px 0 12px;">🎯 Focus Areas for Next Round</h4>
            <div style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px;">
              ${nextSteps.interview_focus_areas.map((a: string) => `<span class="focus-tag">${typeof a === 'string' ? a : a}</span>`).join('')}
            </div>` : ''}

            ${nextSteps.suggested_questions?.length ? `
            <h4 style="margin: 20px 0 12px;">💬 Suggested Questions for Next Round</h4>
            <div style="background: var(--gray-50); border-radius: 8px; padding: 16px; border: 1px solid var(--gray-200);">
              ${nextSteps.suggested_questions.map((q: any, idx: number) => `
                <div style="padding: 12px 0; ${idx > 0 ? 'border-top: 1px solid var(--gray-200);' : ''}">
                  <div style="font-weight: 500; margin-bottom: 4px;">${idx + 1}. "${typeof q === 'string' ? q : q.question}"</div>
                  ${typeof q === 'object' && q.purpose ? `<div style="font-size: 13px; color: var(--gray-500); font-style: italic;">Purpose: ${q.purpose}</div>` : ''}
                </div>
              `).join('')}
            </div>` : ''}

            ${nextSteps.candidate_feedback ? `
            <div class="candidate-feedback">
              <div style="font-weight: 600; color: #166534; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">📝 Feedback for Candidate</div>
              <p style="margin: 0;">${nextSteps.candidate_feedback}</p>
            </div>` : ''}
          </div>

            </div><!-- End report-content -->

            <!-- Footer -->
            <div class="report-footer">
              <div class="footer-info">
                <div><strong>Generated:</strong> ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} at ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
                <div><strong>Report Version:</strong> 2.0 │ <strong>Model:</strong> GPT-4o</div>
              </div>
            </div>
          </div><!-- End report-container -->

          <script>window.print();</script>
        </body>
        </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();

      toast({
        title: 'PDF Ready',
        description: 'Use the print dialog to save as PDF',
        variant: 'success'
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate PDF',
        variant: 'error'
      });
    } finally {
      setIsDownloading(false);
    }
  };
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !report) return null;

  const exec = report.executive_summary || {};
  const candidate = report.candidate_assessment || {};
  const recruiter = report.recruiter_performance || {};
  const logistics = report.candidate_logistics || report.job_fitment_hygiene || {};
  const risk = report.risk_assessment || {};
  const nextSteps = report.next_steps || {};
  const overallSkills = report.overall_skill_analysis || {};

  const getRecommendationColor = (rec: string) => {
    switch (rec) {
      case 'HIGHLY_RECOMMENDED': 
      case 'STRONG_YES': return 'text-green-500 bg-green-500/10';
      case 'RECOMMENDED': 
      case 'YES': return 'text-green-400 bg-green-400/10';
      case 'WORTH_CONSIDERING': 
      case 'MAYBE': return 'text-yellow-500 bg-yellow-500/10';
      case 'NOT_RECOMMENDED': 
      case 'NO': return 'text-orange-400 bg-orange-400/10';
      case 'NEEDS_REASSESSMENT': 
      case 'STRONG_NO': return 'text-amber-500 bg-amber-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getRecommendationLabel = (rec: string) => {
    switch (rec) {
      case 'HIGHLY_RECOMMENDED': return '✨ Highly Recommended';
      case 'STRONG_YES': return '✨ Highly Recommended';
      case 'RECOMMENDED': return '✓ Recommended';
      case 'YES': return '✓ Recommended';
      case 'WORTH_CONSIDERING': return '◐ Worth Considering';
      case 'MAYBE': return '◐ Worth Considering';
      case 'NOT_RECOMMENDED': return '⚠ Not Recommended';
      case 'NO': return '⚠ Not Recommended';
      case 'NEEDS_REASSESSMENT': return '↺ Needs Reassessment';
      case 'STRONG_NO': return '↺ Needs Reassessment';
      default: return rec || 'N/A';
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'PROCEED_TO_TECHNICAL': return '✓ Proceed to Technical Interview';
      case 'SCHEDULE_TECHNICAL': return '✓ Schedule Technical Round';
      case 'PROCEED_TO_CLIENT': return '✓ Proceed to Client Interview';
      case 'SCHEDULE_CLIENT': return '✓ Schedule Client Round';
      case 'ADDITIONAL_SCREENING': return '↺ Additional Screening Needed';
      case 'NEED_MORE_INFO': return '↺ Need More Information';
      case 'KEEP_IN_PIPELINE': return '📋 Keep in Pipeline';
      case 'HOLD': return '⏸ Hold for Review';
      case 'REASSESS_FOR_OTHER_ROLES': return '🔄 Consider for Other Roles';
      case 'NOT_SUITABLE_FOR_ROLE': return '⚠ Not Suitable for This Role';
      case 'REJECT': return '⚠ Not Recommended to Proceed';
      default: return action || 'N/A';
    }
  };

  const getScoreColor = (score: number) => {
    if (score > 70) return 'text-green-500';
    if (score >= 31) return 'text-orange-500';
    return 'text-red-500'; // Below 30
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'LOW': return 'text-green-500 bg-green-500/10';
      case 'MEDIUM': return 'text-yellow-500 bg-yellow-500/10';
      case 'HIGH': return 'text-red-500 bg-red-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  // Use portal to render modal at document body level to avoid z-index stacking context issues
  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-md"
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        zIndex: 99999 
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div 
        className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        style={{ position: 'relative', zIndex: 100000 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Phone className="w-6 h-6 text-blue-600" />
              Candidate Screening Report
            </h2>
            <div className="mt-2 space-y-1">
              <p className="text-gray-700 dark:text-gray-300 text-sm flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="font-medium">Candidate:</span> {displayCandidateName}
              </p>
              <p className="text-gray-700 dark:text-gray-300 text-sm flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-gray-500" />
                <span className="font-medium">Position:</span> {displayRequirementTitle}
              </p>
              <p className="text-gray-700 dark:text-gray-300 text-sm flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="font-medium">Recruiter:</span> {displayRecruiterName}
                <span className="mx-2">│</span>
                <span className="font-medium">Date:</span> {displayInterviewDate}
                <span className="mx-2">│</span>
                <span className="font-medium">Duration:</span> {displayDuration} min
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50 dark:bg-gray-800">
          {/* Executive Summary */}
          <section className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              Executive Summary
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className={`text-3xl font-bold ${getScoreColor(exec.overall_score || 0)}`}>
                  {exec.overall_score || 0}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Overall Score</div>
              </div>
              <div className="text-center p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className={`text-sm font-bold px-2 py-1 rounded ${getRecommendationColor(exec.hire_recommendation)}`}>
                  {getRecommendationLabel(exec.hire_recommendation)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Recommendation</div>
              </div>
              <div className="text-center p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {exec.confidence_level || 'N/A'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Confidence</div>
              </div>
              <div className="text-center p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className={`text-lg font-semibold px-2 py-1 rounded ${getRiskColor(risk.overall_risk_level || risk.risk_level)}`}>
                  {risk.overall_risk_level || risk.risk_level || 'N/A'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Risk Level</div>
              </div>
            </div>

            {exec.one_line_summary && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                <p className="text-gray-700 dark:text-gray-200 font-medium">
                  "{exec.one_line_summary}"
                </p>
              </div>
            )}

            {exec.recommendation_reasoning && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Why This Recommendation:</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {exec.recommendation_reasoning}
                </p>
              </div>
            )}

            {exec.detailed_summary && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                {exec.detailed_summary}
              </p>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {exec.key_highlights?.length > 0 && (
                <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-green-700 dark:text-green-400 mb-2 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> Strengths & Highlights
                  </h4>
                  <ul className="space-y-1.5">
                    {exec.key_highlights.map((item: string, idx: number) => (
                      <li key={idx} className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2">
                        <span className="text-green-500 mt-1">✓</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {exec.key_concerns?.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/10 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Areas to Explore
                  </h4>
                  <ul className="space-y-1.5">
                    {exec.key_concerns.map((item: string, idx: number) => (
                      <li key={idx} className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2">
                        <span className="text-amber-500 mt-1">◐</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {exec.improvement_suggestions?.length > 0 && (
              <div className="mt-4 bg-purple-50 dark:bg-purple-900/10 rounded-lg p-3">
                <h4 className="text-sm font-medium text-purple-700 dark:text-purple-400 mb-2">💡 Suggestions for Improvement</h4>
                <ul className="space-y-1">
                  {exec.improvement_suggestions.map((item: string, idx: number) => (
                    <li key={idx} className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2">
                      <span className="text-purple-500">→</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* Overall Skill Analysis */}
          {(overallSkills.technical || overallSkills.communication || overallSkills.behavioral) && (
            <section className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                📊 Overall Skill Analysis
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className={`text-center p-4 rounded-lg border ${getScoreColor(overallSkills.technical?.score || 0).includes('green') ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : getScoreColor(overallSkills.technical?.score || 0).includes('yellow') ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
                  <div className={`text-3xl font-bold ${getScoreColor(overallSkills.technical?.score || 0)}`}>
                    {overallSkills.technical?.score || 0}
                  </div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-1">Technical</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{overallSkills.technical?.level || 'N/A'}</div>
                </div>
                <div className={`text-center p-4 rounded-lg border ${getScoreColor(overallSkills.communication?.score || 0).includes('green') ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : getScoreColor(overallSkills.communication?.score || 0).includes('yellow') ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
                  <div className={`text-3xl font-bold ${getScoreColor(overallSkills.communication?.score || 0)}`}>
                    {overallSkills.communication?.score || 0}
                  </div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-1">Communication</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{overallSkills.communication?.level || 'N/A'}</div>
                </div>
                <div className={`text-center p-4 rounded-lg border ${getScoreColor(overallSkills.behavioral?.score || 0).includes('green') ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : getScoreColor(overallSkills.behavioral?.score || 0).includes('yellow') ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
                  <div className={`text-3xl font-bold ${getScoreColor(overallSkills.behavioral?.score || 0)}`}>
                    {overallSkills.behavioral?.score || 0}
                  </div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-1">Behavioral</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{overallSkills.behavioral?.level || 'N/A'}</div>
                </div>
              </div>
            </section>
          )}

          {/* Job Fitment & Hygiene */}
          {(report.job_fitment_hygiene || report.candidate_logistics) && (
            <section className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                💼 Job Fitment & Hygiene
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Current CTC', value: (report.job_fitment_hygiene || report.candidate_logistics)?.current_ctc },
                  { label: 'Expected CTC', value: (report.job_fitment_hygiene || report.candidate_logistics)?.expected_ctc },
                  { label: 'Notice Period', value: (report.job_fitment_hygiene || report.candidate_logistics)?.notice_period },
                  { label: 'Preferred Location', value: (report.job_fitment_hygiene || report.candidate_logistics)?.preferred_location || (report.job_fitment_hygiene || report.candidate_logistics)?.location_preference },
                  { label: 'Relocation', value: (report.job_fitment_hygiene || report.candidate_logistics)?.relocation_willingness },
                  { label: 'Availability', value: (report.job_fitment_hygiene || report.candidate_logistics)?.availability },
                  { label: 'Current Company', value: (report.job_fitment_hygiene)?.current_company },
                  { label: 'Total Experience', value: (report.job_fitment_hygiene)?.total_experience },
                  { label: 'Reason for Change', value: (report.job_fitment_hygiene)?.reason_for_change },
                ].filter(item => item.value).map((item, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">{item.label}</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">{item.value || 'N/A'}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Communication Aspects */}
          {report.communication_aspects && (
            <section className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                🗣️ Communication Aspects
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-gray-800">
                      <th className="px-3 py-2 text-left text-gray-700 dark:text-gray-300 font-medium">Parameter</th>
                      <th className="px-3 py-2 text-center text-gray-700 dark:text-gray-300 font-medium">Rating</th>
                      <th className="px-3 py-2 text-left text-gray-700 dark:text-gray-300 font-medium">Observation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {['clarity', 'fluency', 'confidence', 'listening', 'articulation', 'professional_tone'].map((param) => {
                      const data = report.communication_aspects?.[param];
                      if (!data) return null;
                      const label = param.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                      return (
                        <tr key={param} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">{label}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                              data.rating === 'Excellent' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                              data.rating === 'Good' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                              data.rating === 'Average' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                              'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            }`}>
                              {data.rating}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">{data.observation || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 bg-gray-100 dark:bg-gray-800 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">📊 Overall Communication Score</span>
                <span className={`text-lg font-bold ${getScoreColor(report.communication_aspects?.overall_score || 0)}`}>
                  {report.communication_aspects?.overall_score || 0}/100 ({report.communication_aspects?.overall_level || 'N/A'})
                </span>
              </div>
            </section>
          )}

          {/* Behavioral & Cognitive Traits */}
          {report.behavioral_cognitive_traits && (
            <section className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                🧠 Behavioral & Cognitive Traits
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-gray-800">
                      <th className="px-3 py-2 text-left text-gray-700 dark:text-gray-300 font-medium">Trait</th>
                      <th className="px-3 py-2 text-center text-gray-700 dark:text-gray-300 font-medium">Rating</th>
                      <th className="px-3 py-2 text-left text-gray-700 dark:text-gray-300 font-medium">Evidence</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {['proactiveness', 'critical_thinking', 'adaptability', 'team_orientation', 'problem_solving', 'learning_agility', 'ownership'].map((trait) => {
                      const data = report.behavioral_cognitive_traits?.[trait];
                      if (!data) return null;
                      const label = trait.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                      return (
                        <tr key={trait} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">{label}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                              data.rating === 'Excellent' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                              data.rating === 'Good' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                              data.rating === 'Average' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                              'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            }`}>
                              {data.rating}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">{data.evidence || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 bg-gray-100 dark:bg-gray-800 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">📊 Overall Behavioral Score</span>
                <span className={`text-lg font-bold ${getScoreColor(report.behavioral_cognitive_traits?.overall_score || 0)}`}>
                  {report.behavioral_cognitive_traits?.overall_score || 0}/100 ({report.behavioral_cognitive_traits?.overall_level || 'N/A'})
                </span>
              </div>
            </section>
          )}

          {/* Conversation Analysis */}
          {report.conversation_analysis?.question_responses?.length > 0 && (
            <section className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                💬 Conversation Analysis
                <span className="ml-auto text-sm font-normal text-gray-500 dark:text-gray-400">
                  {report.conversation_analysis?.total_questions_analyzed || report.conversation_analysis?.question_responses?.length || 0} Questions │ {report.conversation_analysis?.skills_discussed?.length || 0} Skills
                </span>
              </h3>
              
              <div className="space-y-4">
                {report.conversation_analysis.question_responses.map((qa: any, idx: number) => (
                  <div key={idx} className={`border rounded-lg overflow-hidden ${qa.response_quality === 'NOT_ANSWERED' || qa.response_quality === 'POOR' ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10' : 'border-gray-200 dark:border-gray-700'}`}>
                    <div className={`px-4 py-2 flex items-center justify-between ${qa.response_quality === 'NOT_ANSWERED' || qa.response_quality === 'POOR' ? 'bg-red-100 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-gray-800'}`}>
                      <span className="font-semibold text-primary dark:text-blue-400">📌 SKILL: {qa.skill}</span>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                        qa.response_quality === 'EXCELLENT' || qa.response_quality === 'GOOD' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        qa.response_quality === 'PARTIAL' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {qa.response_quality === 'GOOD' ? '✓ GOOD' : qa.response_quality === 'EXCELLENT' ? '★ EXCELLENT' : qa.response_quality === 'PARTIAL' ? '⚠️ PARTIAL' : '❌ ' + qa.response_quality}
                      </span>
                    </div>
                    <div className="p-4 space-y-3">
                      <div>
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">❓ Question Asked</div>
                        <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded text-sm text-gray-700 dark:text-gray-300">"{qa.question_asked}"</div>
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">💬 Candidate Response</div>
                        <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded text-sm text-gray-700 dark:text-gray-300">"{qa.candidate_response}"</div>
                      </div>
                      {qa.evaluation && (
                        <div className="grid grid-cols-5 gap-2 mt-3">
                          {Object.entries(qa.evaluation).map(([key, val]: [string, any]) => (
                            <div key={key} className="text-center p-2 bg-gray-50 dark:bg-gray-800 rounded">
                              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">{key.replace(/_/g, ' ').slice(0, 12)}</div>
                              <div className={`text-xs font-semibold mt-1 ${
                                val?.rating === 'Excellent' || val?.rating === 'Good' ? 'text-green-600 dark:text-green-400' :
                                val?.rating === 'Average' ? 'text-yellow-600 dark:text-yellow-400' :
                                'text-red-600 dark:text-red-400'
                              }`}>{val?.rating || 'N/A'}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {qa.overall_assessment && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-3 border-blue-500 p-3 rounded-r text-sm">
                          <strong className="text-blue-700 dark:text-blue-400">💡 Assessment:</strong> {qa.overall_assessment}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Conversation Summary */}
              {report.conversation_analysis?.summary && (
                <div className="grid grid-cols-4 gap-3 mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{report.conversation_analysis.summary.total_questions || 0}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Total</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">{report.conversation_analysis.summary.answered_well || 0}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Good</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{report.conversation_analysis.summary.answered_partially || 0}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Partial</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">{report.conversation_analysis.summary.not_answered || 0}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Gap</div>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Skills Scorecard */}
          {report.skills_scorecard && (
            <section className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                📊 Skills Scorecard (Based on JD Requirements)
              </h3>

              {/* Primary Skills Table */}
              {report.skills_scorecard.primary_skills?.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Primary Skills ({report.skills_scorecard.primary_skills_weight || 80}% of score)
                    </h4>
                    <span className={`text-lg font-bold ${getScoreColor(report.skills_scorecard.primary_skills_score || 0)}`}>
                      {report.skills_scorecard.primary_skills_score || 0}%
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100 dark:bg-gray-800">
                          <th className="px-3 py-2 text-left text-gray-700 dark:text-gray-300 font-medium">Skill</th>
                          <th className="px-3 py-2 text-center text-gray-700 dark:text-gray-300 font-medium">Importance</th>
                          <th className="px-3 py-2 text-center text-gray-700 dark:text-gray-300 font-medium">Status</th>
                          <th className="px-3 py-2 text-center text-gray-700 dark:text-gray-300 font-medium">Score</th>
                          <th className="px-3 py-2 text-center text-gray-700 dark:text-gray-300 font-medium">Impact</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {report.skills_scorecard.primary_skills.map((skill: any, idx: number) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-900 dark:text-white">{skill.skill_name}</div>
                              {skill.evidence && skill.evidence !== 'Not mentioned in call' && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 italic mt-1">"{skill.evidence}"</div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className={`px-2 py-0.5 text-xs rounded ${
                                skill.importance === 'CRITICAL' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                                skill.importance === 'HIGH' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
                                'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                              }`}>
                                {skill.importance}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {skill.demonstrated ? (
                                <span className="text-green-500">✓ Demonstrated</span>
                              ) : (
                                <span className="text-red-500">✗ Not shown</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className={`font-bold ${getScoreColor(skill.score || 0)}`}>
                                {skill.score || 0}%
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {(() => {
                                const maxPts = skill.weight_percent || (skill.importance === 'CRITICAL' ? 40 : skill.importance === 'HIGH' ? 25 : 15);
                                const impactMatch = (skill.score_impact || '0').match(/([+-]?\d+)/);
                                const impactPts = impactMatch ? parseInt(impactMatch[1]) : 0;
                                const impactDisplay = impactPts >= 0 ? `+${Math.abs(impactPts)}/${maxPts}` : `${impactPts}/${maxPts}`;
                                return (
                              <span className={`font-medium ${
                                    impactPts > 0 ? 'text-green-600 dark:text-green-400' :
                                    impactPts < 0 ? 'text-red-600 dark:text-red-400' :
                                'text-gray-500 dark:text-gray-400'
                              }`}>
                                    {impactDisplay} pts
                              </span>
                                );
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Secondary Skills Table */}
              {report.skills_scorecard.secondary_skills?.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Secondary Skills ({report.skills_scorecard.secondary_skills_weight || 20}% of score)
                    </h4>
                    <span className={`text-lg font-bold ${getScoreColor(report.skills_scorecard.secondary_skills_score || 0)}`}>
                      {report.skills_scorecard.secondary_skills_score || 0}%
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100 dark:bg-gray-800">
                          <th className="px-3 py-2 text-left text-gray-700 dark:text-gray-300 font-medium">Skill</th>
                          <th className="px-3 py-2 text-center text-gray-700 dark:text-gray-300 font-medium">Status</th>
                          <th className="px-3 py-2 text-center text-gray-700 dark:text-gray-300 font-medium">Score</th>
                          <th className="px-3 py-2 text-center text-gray-700 dark:text-gray-300 font-medium">Impact</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {report.skills_scorecard.secondary_skills.map((skill: any, idx: number) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">{skill.skill_name}</td>
                            <td className="px-3 py-2 text-center">
                              {skill.demonstrated ? (
                                <span className="text-green-500">✓</span>
                              ) : (
                                <span className="text-red-500">✗</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className={`font-bold ${getScoreColor(skill.score || 0)}`}>
                                {skill.score || 0}%
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {(() => {
                                const maxPts = skill.weight_percent || 15;
                                const impactMatch = (skill.score_impact || '0').match(/([+-]?\d+)/);
                                const impactPts = impactMatch ? parseInt(impactMatch[1]) : 0;
                                const impactDisplay = impactPts >= 0 ? `+${Math.abs(impactPts)}/${maxPts}` : `${impactPts}/${maxPts}`;
                                return (
                              <span className={`font-medium ${
                                    impactPts > 0 ? 'text-green-600 dark:text-green-400' :
                                    impactPts < 0 ? 'text-red-600 dark:text-red-400' :
                                'text-gray-500 dark:text-gray-400'
                              }`}>
                                    {impactDisplay} pts
                              </span>
                                );
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Coverage Summary */}
              {report.skills_scorecard.skills_coverage_summary && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mt-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Coverage:</strong> {report.skills_scorecard.skills_coverage_summary}
                  </p>
                </div>
              )}

              {/* Critical Gaps */}
              {report.skills_scorecard.critical_gaps?.length > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mt-4">
                  <h5 className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">⚠️ Critical Skill Gaps</h5>
                  <div className="flex flex-wrap gap-2">
                    {report.skills_scorecard.critical_gaps.map((gap: string, idx: number) => (
                      <span key={idx} className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded">
                        {gap}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Score Calculation Breakdown - NEW */}
          {report.score_calculation && (
            <section className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                📐 Score Calculation Breakdown
              </h3>

              {/* Final Score Display */}
              <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-800 rounded-lg p-4 mb-4">
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Final Calculated Score</div>
                  <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {report.score_calculation.calculation_formula || `Base: ${report.score_calculation.base_score}`}
                  </div>
                </div>
                <div className={`text-4xl font-bold ${getScoreColor(report.score_calculation.final_score || 0)}`}>
                  {report.score_calculation.final_score || 0}%
                </div>
              </div>

              {/* Score Breakdown Bar */}
              {report.score_calculation.score_breakdown && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Score Contribution</h4>
                  <div className="space-y-2">
                    {Object.entries(report.score_calculation.score_breakdown).map(([key, value]: [string, any]) => (
                      <div key={key} className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400 w-32 truncate">
                          {key.replace(/_/g, ' ').replace('contribution', '')}
                        </span>
                        <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${value >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(Math.abs(value as number), 100)}%` }}
                          />
                        </div>
                        <span className={`text-xs font-medium w-12 text-right ${(value as number) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {(value as number) >= 0 ? '+' : ''}{value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deductions */}
              {report.score_calculation.deductions?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">📉 Deductions</h4>
                  <div className="space-y-2">
                    {report.score_calculation.deductions.map((d: any, idx: number) => (
                      <div key={idx} className="flex items-start justify-between bg-red-50 dark:bg-red-900/10 rounded-lg p-2">
                        <div className="flex-1">
                          <div className="text-sm text-gray-900 dark:text-white">{d.reason}</div>
                          {d.details && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{d.details}</div>}
                        </div>
                        <span className="text-red-600 dark:text-red-400 font-bold ml-2">{d.points} pts</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additions */}
              {report.score_calculation.additions?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-green-600 dark:text-green-400 mb-2">📈 Additions</h4>
                  <div className="space-y-2">
                    {report.score_calculation.additions.map((a: any, idx: number) => (
                      <div key={idx} className="flex items-start justify-between bg-green-50 dark:bg-green-900/10 rounded-lg p-2">
                        <div className="flex-1">
                          <div className="text-sm text-gray-900 dark:text-white">{a.reason}</div>
                          {a.details && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{a.details}</div>}
                        </div>
                        <span className="text-green-600 dark:text-green-400 font-bold ml-2">+{a.points} pts</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Candidate Assessment */}
          <section className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Candidate Assessment
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
              <div className="text-center p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className={`text-xl font-bold ${getScoreColor(candidate.overall_fit_score || candidate.skills_match_score || 0)}`}>
                  {candidate.overall_fit_score || candidate.skills_match_score || 0}%
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Overall Fit</div>
              </div>
              <div className="text-center p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className={`text-xl font-bold ${getScoreColor(candidate.skills_match_score || 0)}`}>
                  {candidate.skills_match_score || 0}%
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Skills</div>
              </div>
              <div className="text-center p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className={`text-xl font-bold ${getScoreColor(candidate.experience_match_score || 0)}`}>
                  {candidate.experience_match_score || 0}%
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Experience</div>
              </div>
              <div className="text-center p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className={`text-xl font-bold ${getScoreColor(candidate.technical_depth_score || 0)}`}>
                  {candidate.technical_depth_score || 0}%
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Tech Depth</div>
              </div>
              <div className="text-center p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className={`text-xl font-bold ${getScoreColor(candidate.communication_score || 0)}`}>
                  {candidate.communication_score || 0}%
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Communication</div>
              </div>
              <div className="text-center p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  {candidate.experience_level || 'N/A'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Level</div>
              </div>
            </div>

            {/* Detailed Analysis */}
            {(candidate.fit_explanation || candidate.skills_analysis) && (
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                {candidate.fit_explanation && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Overall Fit Analysis</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{candidate.fit_explanation}</p>
                  </div>
                )}
                {candidate.skills_analysis && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Skills Analysis</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{candidate.skills_analysis}</p>
                  </div>
                )}
              </div>
            )}

            {/* Growth Potential */}
            {candidate.growth_potential && (
              <div className="mb-4 flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Growth Potential:</span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                  candidate.growth_potential === 'HIGH' ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400' :
                  candidate.growth_potential === 'MEDIUM' ? 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400' :
                  'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
                }`}>
                  {candidate.growth_potential}
                </span>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              {candidate.mandatory_skills_covered?.length > 0 && (
                <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-green-700 dark:text-green-400 mb-2">✓ Skills Confirmed</h4>
                  <div className="flex flex-wrap gap-1">
                    {candidate.mandatory_skills_covered.map((skill: string, idx: number) => (
                      <span key={idx} className="px-2 py-0.5 bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 text-xs rounded">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {candidate.skill_gaps?.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/10 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-2">◐ Skills to Verify</h4>
                  <div className="flex flex-wrap gap-1">
                    {candidate.skill_gaps.map((skill: string, idx: number) => (
                      <span key={idx} className="px-2 py-0.5 bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 text-xs rounded">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Evidence Section */}
            {candidate.evidence?.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">📋 Evidence from Call</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {candidate.evidence.slice(0, 5).map((e: any, idx: number) => (
                    <div key={idx} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900 dark:text-white">{e.skill}</span>
                        {e.demonstrated_level && (
                          <span className={`px-1.5 py-0.5 text-xs rounded ${
                            e.demonstrated_level === 'EXPERT' || e.demonstrated_level === 'ADVANCED' ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400' :
                            e.demonstrated_level === 'INTERMEDIATE' ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400' :
                            'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
                          }`}>
                            {e.demonstrated_level}
                          </span>
                        )}
                      </div>
                      {e.quote && (
                        <p className="text-gray-500 dark:text-gray-400 italic text-xs mb-1">"{e.quote}"</p>
                      )}
                      <p className="text-gray-600 dark:text-gray-300">{e.assessment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Strengths & Development Areas */}
            {(candidate.strengths_summary || candidate.development_areas) && (
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                {candidate.strengths_summary && (
                  <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-1">Core Strengths</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{candidate.strengths_summary}</p>
                  </div>
                )}
                {candidate.development_areas && (
                  <div className="bg-purple-50 dark:bg-purple-900/10 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-purple-700 dark:text-purple-400 mb-1">Development Areas</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{candidate.development_areas}</p>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Recruiter Performance */}
          <section className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-purple-500" />
              Recruiter Performance
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="text-center p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className={`text-xl font-bold ${getScoreColor(recruiter.overall_call_quality || recruiter.call_quality_score || 0)}`}>
                  {recruiter.overall_call_quality || recruiter.call_quality_score || 0}%
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Call Quality</div>
              </div>
              <div className="text-center p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className={`text-xl font-bold ${getScoreColor(recruiter.script_adherence_score || 0)}`}>
                  {recruiter.script_adherence_score || 0}%
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Script</div>
              </div>
              <div className="text-center p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className={`text-xl font-bold ${getScoreColor(recruiter.questioning_technique_score || 0)}`}>
                  {recruiter.questioning_technique_score || 0}%
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Questions</div>
              </div>
              <div className="text-center p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className={`text-xl font-bold ${getScoreColor(recruiter.active_listening_score || 0)}`}>
                  {recruiter.active_listening_score || 0}%
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Listening</div>
              </div>
              <div className="text-center p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className={`text-xl font-bold ${getScoreColor(recruiter.professionalism_score || 0)}`}>
                  {recruiter.professionalism_score || 0}%
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Professional</div>
              </div>
            </div>

            {recruiter.areas_for_improvement?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-2">Areas for Improvement</h4>
                <ul className="space-y-1">
                  {recruiter.areas_for_improvement.map((item: string, idx: number) => (
                    <li key={idx} className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2">
                      <span className="text-amber-400">•</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* Candidate Logistics */}
          <section className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-cyan-500" />
              Candidate Logistics
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Interest Level</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{logistics.interest_level || 'N/A'}</div>
              </div>
              <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Notice Period</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{logistics.notice_period || 'N/A'}</div>
              </div>
              <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current CTC</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{logistics.current_ctc || 'N/A'}</div>
              </div>
              <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Expected CTC</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{logistics.expected_ctc || 'N/A'}</div>
              </div>
              <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Location</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{logistics.location_preference || 'N/A'}</div>
              </div>
              <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Relocation</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{logistics.relocation_willingness || 'N/A'}</div>
              </div>
              <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg col-span-2">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Availability</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{logistics.availability || 'N/A'}</div>
              </div>
            </div>
          </section>

          {/* Next Steps */}
          <section className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-500" />
              Next Steps & Recommendations
            </h3>
            
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className={`px-4 py-2 rounded-lg font-medium ${
                nextSteps.primary_recommendation?.includes('PROCEED') || nextSteps.recommended_action?.includes('SCHEDULE') 
                  ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400'
                  : nextSteps.primary_recommendation?.includes('ADDITIONAL') || nextSteps.recommended_action?.includes('HOLD')
                  ? 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400'
                  : nextSteps.primary_recommendation?.includes('NOT_SUITABLE') || nextSteps.recommended_action?.includes('REJECT')
                  ? 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400'
                  : 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400'
              }`}>
                {getActionLabel(nextSteps.primary_recommendation || nextSteps.recommended_action)}
              </div>
              <div className={`px-3 py-1 rounded text-sm ${
                nextSteps.urgency === 'URGENT' || nextSteps.urgency === 'HIGH' ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400' :
                nextSteps.urgency === 'MEDIUM' ? 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400' :
                'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
              }`}>
                {nextSteps.urgency || 'N/A'} Priority
              </div>
            </div>

            {nextSteps.recommendation_details && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">{nextSteps.recommendation_details}</p>
              </div>
            )}

            {nextSteps.alternative_recommendation && (
              <div className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                <span className="font-medium">Alternative:</span> {nextSteps.alternative_recommendation}
              </div>
            )}

            {nextSteps.interview_focus_areas?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">🎯 Focus Areas for Next Round</h4>
                <div className="flex flex-wrap gap-2">
                  {nextSteps.interview_focus_areas.map((area: string, idx: number) => (
                    <span key={idx} className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-sm rounded border border-blue-200 dark:border-blue-800">
                      {typeof area === 'string' ? area : area}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {nextSteps.suggested_questions?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">💬 Suggested Questions for Next Round</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {nextSteps.suggested_questions.map((q: any, idx: number) => (
                    <div key={idx} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-sm">
                      <p className="font-medium text-gray-900 dark:text-white mb-1">
                        {idx + 1}. {typeof q === 'string' ? q : q.question}
                      </p>
                      {typeof q === 'object' && q.purpose && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">Purpose: {q.purpose}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {nextSteps.action_items?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">📋 Action Items</h4>
                <div className="space-y-2">
                  {nextSteps.action_items.map((item: any, idx: number) => (
                    <div key={idx} className="flex items-start gap-3 bg-gray-50 dark:bg-gray-800 p-2 rounded-lg text-sm">
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        item.priority === 'HIGH' ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400' :
                        item.priority === 'MEDIUM' ? 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400' :
                        'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
                      }`}>
                        {item.priority || 'MEDIUM'}
                      </span>
                      <div className="flex-1">
                        <span className="text-gray-900 dark:text-white">{item.action}</span>
                        <span className="text-gray-500 dark:text-gray-400 text-xs ml-2">({item.owner} • {item.deadline})</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {nextSteps.candidate_feedback && (
              <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-3">
                <h4 className="text-sm font-medium text-green-700 dark:text-green-400 mb-1">📝 Feedback for Candidate</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">{nextSteps.candidate_feedback}</p>
              </div>
            )}
          </section>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900">
          <Button 
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDownloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download PDF
          </Button>
          <Button 
            variant="outline" 
            onClick={onClose}
            className="border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium px-5 py-2.5 rounded-lg transition-all duration-200"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );

  // Render modal using portal to escape any parent stacking context
  return createPortal(modalContent, document.body);
};



// Version: 2025-12-12-v2 - Production PDF format update
